import type { i18n as I18n } from "i18next";

import type { Hour, Minute } from "@braze/beacon-lab-time-picker/dist/components/TimePicker";

/**
 * Time units denominated in seconds
 */
export const TimeUnit = {
  Second: 1,
  Minute: 60,
  Hour: 3600,
  Day: 86400,
  CalendarDay: 86400,
  Week: 604800,
} as const;

export const DELAY_MULTIPLIER_TO_TIME_UNIT = {
  [TimeUnit.Second]: "Second",
  [TimeUnit.Minute]: "Minute",
  [TimeUnit.Hour]: "Hour",
  [TimeUnit.Day]: "Day",
  [TimeUnit.Week]: "Week",
} as const;

/**
 * Maps TimeUnitStrings to Ruby time unit strings
 * Used for serializing time units to the backend
 */
export const timeUnitToRubyTimeUnitLookup: { [K in TimeUnitStrings]: string } = {
  Second: "seconds",
  Minute: "minutes",
  Hour: "hours",
  Day: "days",
  CalendarDay: "calendar_days",
  Week: "weeks",
};

/**
 * @note this only differs from `TimeUnitKey` with the inclusion of `CalendarDay`
 */
export type TimeUnitStrings = keyof typeof TimeUnit;

export type TimeUnitKey = Exclude<TimeUnitStrings, "CalendarDay">;

//#region typings and utilities for time strings

/**
 * a HH:mm time string that is the part of an ISO date string
 */
export type ISOTimeString = `${number}:${number}`;

/**
 * a YYYY-MM-DD date string
 */
export type ISODateString = `${number}-${number}-${number}`;

/**
 * a yyyy-MM-ddTHH:mm:ssZ iso8601 date string
 * @note this is too complicated to represent as a computed type union, so we are simply constraining the use to the
 * formatting parts.
 */
export type ISODateTimeStringZulu = `${ISODateString}T${ISOTimeString}:${number}Z`;

/**
 * a YYYY-MM-DDTHH:mm date time string
 * @note this is too complicated to represent as a computed type union, so we are simply
 *   constraining the use to the formatting parts.
 */
export type ISODateTimeString = `${ISODateString}T${ISOTimeString}`;

/**
 * a YYYY-MM-DDTHH:mm:SS.MMM(+|-)hh:mm ISO8601 date string used to specify:
 * - date
 * - time
 * - time zone offset
 * @note this is too compilcated to represent as a computed type union, so we are simply
 *   constraining the use to the formatting parts.
 */
export type ISODateTimeTzString =
  `${ISODateString}T${ISOTimeString}:${number}.${number}${"+" | "-"}${ISOTimeString}`;

//#endregion

/**
 * @note this should not be used for localization purposes, rather for ordering or
 *   iterating through values of `Weekday` easily
 *
 * @note Sunday is the first day of the week in the backend, so we need to put it first in the array
 * to avoid weird conversions between the backend and the frontend, e.g., index-based operations to day-of-week string.
 */
export const WeekdayStrings = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

/**
 * @note this should be preferred over the use of `enum DayOfWeek`
 */
export type Weekday = (typeof WeekdayStrings)[number];

export interface TimeParams {
  hours: Hour;
  minutes: Minute;
}

export interface DateTimeParams {
  date: ISODateString;
  time: TimeParams;
}

type NonTimeUnitFormats = "auto" | "natural";

/**
 * type to describe the `formatDuration` method
 * @note `timeUnit` should probably be made option because variadic args bad
 */
export type FormatDuration = (
  value: number,
  timeUnit: TimeUnitKey | NonTimeUnitFormats,
  options?: { locale?: I18n["language"] }
) => string;
